import { useState } from "react";
import {
  AlertTriangle, Check, ClipboardCheck, ExternalLink, FileWarning, Receipt, Undo2, X,
} from "lucide-react";

import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

/**
 * Shared pieces of the claims interface.
 *
 * Extracted so the review queue and the dashboard render a claim identically.
 * Two screens drifting apart on how they show a missing receipt is exactly the
 * kind of small inconsistency that erodes trust in a money screen.
 */

const STATUS_STYLES = {
  submitted: { label: "Awaiting review", tone: "bg-amber-500/10 text-amber-600" },
  approved: { label: "Approved", tone: "bg-blue-500/10 text-blue-600" },
  queried: { label: "Queried", tone: "bg-violet-500/10 text-violet-600" },
  rejected: { label: "Declined", tone: "bg-destructive/10 text-destructive" },
  paid: { label: "Paid", tone: "bg-emerald-500/10 text-emerald-600" },
};

export function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.submitted;
  return <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${s.tone}`}>{s.label}</span>;
}

export function ReceiptLink({ claim, token, api }) {
  if (!claim.receipt_path) {
    return (
      <p className="text-xs text-amber-600 flex items-start gap-1.5"
         data-testid={`no-receipt-${claim.claim_id}`}>
        <FileWarning className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
        No receipt — {claim.no_receipt_reason || "no reason given"}
      </p>
    );
  }
  return (
    <a href={`${api}/files/${claim.receipt_path}?auth=${token}`}
       target="_blank" rel="noopener noreferrer"
       className="text-xs font-medium text-primary inline-flex items-center gap-1.5
                  rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1.5 hover:bg-primary/10"
       data-testid={`receipt-${claim.claim_id}`}>
      <Receipt className="h-3.5 w-3.5" aria-hidden="true" />
      Open receipt
      <ExternalLink className="h-3 w-3 opacity-70" aria-hidden="true" />
    </a>
  );
}

export function ReviewCard({ claim, token, api, onDecide }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState(claim.amount);

  const decide = (decision) => {
    onDecide(claim, {
      decision,
      note,
      // Only send an adjusted figure when it actually differs, so the common
      // case stays a single tap.
      approved_amount: decision === "approved" && amount !== claim.amount ? amount : undefined,
    });
  };

  return (
    <Card data-testid={`claim-${claim.claim_id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-foreground">
              ${claim.amount}
              <span className="font-normal text-muted-foreground"> · {claim.claimant_name}</span>
            </p>
            <p className="text-sm text-muted-foreground">{claim.description}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {claim.event_title} · {claim.category}
              {claim.spent_on ? ` · spent ${claim.spent_on}` : ""}
            </p>
          </div>
          <StatusBadge status={claim.status} />
        </div>

        {claim.over_pre_approval_threshold && (
          <p className="text-xs text-amber-600 flex items-start gap-1.5"
             data-testid={`over-threshold-${claim.claim_id}`}>
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
            Above the pre-approval limit — worth a second look
          </p>
        )}

        <ReceiptLink claim={claim} token={token} api={api} />

        {claim.decision_note && (
          <p className="text-xs bg-muted rounded-md p-2 text-muted-foreground">
            {claim.decided_by_name}: {claim.decision_note}
          </p>
        )}

        {(claim.status === "submitted" || claim.status === "queried") && (
          open ? (
            <div className="space-y-3 pt-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor={`amt-${claim.claim_id}`} className="text-xs">
                    Approve amount
                  </Label>
                  <Input id={`amt-${claim.claim_id}`} className="h-11" value={amount}
                         onChange={(e) => setAmount(e.target.value)}
                         data-testid={`amount-${claim.claim_id}`} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`note-${claim.claim_id}`} className="text-xs">
                  Note to the claimant (optional)
                </Label>
                <Textarea id={`note-${claim.claim_id}`} rows={2} value={note}
                          onChange={(e) => setNote(e.target.value)}
                          data-testid={`note-${claim.claim_id}`} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Button className="h-11 gap-1.5" onClick={() => decide("approved")}
                        data-testid={`approve-${claim.claim_id}`}>
                  <Check className="h-4 w-4" aria-hidden="true" />Approve
                </Button>
                <Button variant="outline" className="h-11 gap-1.5"
                        onClick={() => decide("queried")}
                        data-testid={`query-${claim.claim_id}`}>
                  <Undo2 className="h-4 w-4" aria-hidden="true" />Query
                </Button>
                <Button variant="outline" className="h-11 gap-1.5 text-destructive"
                        onClick={() => decide("rejected")}
                        data-testid={`reject-${claim.claim_id}`}>
                  <X className="h-4 w-4" aria-hidden="true" />Decline
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" className="h-10 gap-1.5"
                    onClick={() => setOpen(true)}
                    data-testid={`review-${claim.claim_id}`}>
              <ClipboardCheck className="h-4 w-4" aria-hidden="true" />
              Approve or reject
            </Button>
          )
        )}
      </CardContent>
    </Card>
  );
}

export { STATUS_STYLES };
